import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  useExportCISStatementOfEarnings,
  useGetEarningReportList,
  useSendMultipleMailCISStatementOfEarnings,
} from '@nxsys/hooks/api/report';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetCISStateOfEarningsListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import EarningReportFilterModal, {
  EarningReportFilterValues,
} from '../components/EarningReportFilterModal';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCISStateOfEarningsColumns } from './columns';

const CISStateOfEarningReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { cisStateOfEarningsList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = cisStateOfEarningsList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IEarningReport[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { run: getEarningReportList, loading: isGetEarningReportListLoading } =
    useGetEarningReportList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.cisStateOfEarnings']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.sendEmail']}
          variant="outlined"
          onClick={handleSendMailSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetCISStateOfEarningsListParams) => {
      const newParams = {
        ...params,
      };

      dispatch(reportActions.getCISStateOfEarningsList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: EarningReportFilterValues) => {
    const params: GetCISStateOfEarningsListParams = {
      ...queryParams,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const {
    run: sendMultipleMailCISStatementOfEarnings,
    loading: isSendMultipleMailCISStatementOfEarningsLoading,
  } = useSendMultipleMailCISStatementOfEarnings();
  useNotificationSocket({
    eventName: `send-mail-earning-summary-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.emailCISStatementOfEarningsSendCompleted'],
      });
      refetchData();
    },
  });
  const handleSendMailMultiple = (data: IEarningReport[]) => {
    const params = data.map((item) => ({
      subcontractorId: item.id,
      companyId: item.company_id,
      taxPeriod: item.taxPeriod,
      taxYear: item.taxYear,
    }));
    sendMultipleMailCISStatementOfEarnings({ dataEmails: params }).then(
      (res) => {
        if (res.success) {
          showMessage({
            type: 'info',
            content: messages['common.emailCISStatementOfEarningsSending'],
          });
        }
      },
    );
  };
  const handleSendMailSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendMailMultiple(selectedRows);
  };
  const handleSendMail = (item: IEarningReport) => {
    handleSendMailMultiple([item]);
  };

  const {
    run: exportCISStatementOfEarnings,
    loading: isExportCISStatementOfEarningsLoading,
  } = useExportCISStatementOfEarnings();
  const handleExportMultiple = (data: IEarningReport[]) => {
    exportCISStatementOfEarnings({
      id: data.map((item) => item.id).join(','),
      companyId: data.map((item) => item.company_id).join(','),
      taxPeriod: data.map((item) => item.taxPeriod).join(','),
      taxYear: data[0].taxYear,
    }).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      }
    });
  };
  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExportMultiple(selectedRows);
  };
  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getEarningReportList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExportMultiple(res.data);
    });
  };
  const handleExport = (item: IEarningReport) => {
    handleExportMultiple([item]);
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isSendMultipleMailCISStatementOfEarningsLoading ||
        isExportCISStatementOfEarningsLoading ||
        isGetEarningReportListLoading
      }
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id_index"
            data={
              list?.map((item, index) => ({ ...item, id_index: index })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2500, y: 700 }}
            columns={getCISStateOfEarningsColumns({
              messages,
              payrollCountry,
              handleSendMail,
              handleExport,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <EarningReportFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default CISStateOfEarningReport;
